import PropTypes from 'prop-types';
// material-ui
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import MainCard from 'components/MainCard';

/**
 * SentimentAnalyticsCard
 *
 * A compact metric card that renders one of four variants:
 * - 'sentiment'  -> "85% Positive"
 * - 'rating'     -> "4.6/5"
 * - 'volume'     -> "1,245"
 * - 'critical'   -> "38"
 *
 * `trend` shows comparison to the previous period.
 */
export default function SentimentAnalyticsCard({
    variant = 'sentiment',
    color = 'primary',
    title,
    value,
    trend
}) {
    const getDisplayValue = () => {
        if (value == null) return '—';

        switch (variant) {
            case 'sentiment':
                return `${value}% Positive`;
            case 'rating':
                return `${value}/5`;
            case 'volume':
            case 'critical':
            default:
                return String(value);
        }
    };

    return (
        <MainCard contentSX={{ p: 2.25 }}>
            <Stack spacing={0.75}>
                <Typography variant="h6" color="text.secondary">
                    {title}
                </Typography>

                <Grid container alignItems="center">
                    <Grid item>
                        <Typography variant="h4" color="inherit">
                            {getDisplayValue()}
                        </Typography>
                    </Grid>
                </Grid>

                {typeof trend !== 'undefined' && trend !== null && (
                    <Box sx={{ pt: 2.25 }}>
                        <Typography variant="caption" color="text.secondary">
                            <Typography
                                component="span"
                                variant="caption"
                                sx={{ color: `${color}.main` }}
                            >
                                {trend}
                            </Typography>{' '}
                            vs last period
                        </Typography>
                    </Box>
                )}
            </Stack>
        </MainCard>
    );
}

SentimentAnalyticsCard.propTypes = {
    /** One of: 'sentiment' | 'rating' | 'volume' | 'critical' */
    variant: PropTypes.oneOf(['sentiment', 'rating', 'volume', 'critical']),
    /** Title shown above the metric */
    title: PropTypes.string.isRequired,
    /** Numeric/string value shown as the main metric (e.g., 85, 4.6, 1245) */
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    /** Comparison text/number (e.g., "+4%", "-0.2") */
    trend: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    /** MUI color key used for the trend highlight */
    color: PropTypes.string
};
